import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import Teacher from "../../types/core/Teacher";
import Contact from "../../types/core/Contact";
import { saveContacts } from "./Contact";

// Define the type for our state store entry
type TeacherState = {
    archives: Teacher[];
    teachers: Teacher[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: TeacherState = {
    archives: [],
    teachers: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const teacherSlice = createSlice({
    name: "teacher",
    initialState: initialState,
    reducers: {
        resetTeacherStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.teacher?.archives?.map(c => new Teacher(c)) ||
                        [],
                    teachers:
                        rawData?.payload?.teacher?.teachers?.map(c => new Teacher(c)) ||
                        [],
                    status: !rawData?.payload?.teacher?.teachers ? "idle" : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchTeachers.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchTeachers.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.teachers = action.payload[0].map(c => new Teacher(c));
                state.archives = action.payload[1].map(c => new Teacher(c));
            })
            .addCase(fetchTeachers.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addTeacher.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addTeacher.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.teachers = state.teachers.concat(
                    action.payload.map(c => new Teacher(c)),
                );
            })
            .addCase(addTeacher.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateTeacher.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateTeacher.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const teacherIds = action.payload.map(c => c.id);
                state.teachers = state.teachers
                    .filter(c => !teacherIds.includes(c.id))
                    .concat(action.payload.map(c => new Teacher(c)));
            })
            .addCase(updateTeacher.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveTeacher.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveTeacher.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const teacherIds = action.payload.map(c => c.id);
                state.teachers = state.teachers.filter(
                    c => !teacherIds.includes(c.id),
                );
                state.archives = state.archives.concat(
                    action.payload.map(c => new Teacher(c)),
                );
            })
            .addCase(archiveTeacher.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetTeacherStatus } = teacherSlice.actions;

export const initialiseTeacherDetails = createAsyncThunk(
    "teacher/initialiseTeacherDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchTeachers()),
);

// This is a thunk, they are used to load the state
export const fetchTeachers = createAsyncThunk(
    "teacher/fetchTeachers",
    () =>
        Promise.all([
            Datastore.getType<Teacher[]>("api/Teachers"),
            Datastore.getType<Teacher[]>("api/Teachers/Archive"),
        ]),
    {
        condition: (_, api) =>
            store.getState().db.teacher.status === "idle",
    },
);
export const addTeacher = createAsyncThunk(
    "teacher/addTeacher",
    async (payload: Teacher[]) => {
        const temp = new Map<Teacher, Contact[]>();
        for (const teacher of payload) {
            temp.set(teacher, teacher.contacts ?? []);
            delete teacher.contacts;
        }
        const waitContacts = Array.from(temp.values()).flat();

        const teachers = await Datastore.postType<Teacher[]>(
            "api/Teachers",
            payload,
        );

        if (waitContacts.length) {
            const transmitContacts: Contact[] = [];
            const lookup = Array.from(temp.entries());
            for (const contact of waitContacts) {
                const sendTeacher = lookup.find(([_, contacts]) =>
                    contacts.includes(contact),
                )?.[0];
                if (!sendTeacher) continue;
                const responseTeacher = teachers.find(
                    t => t.code === sendTeacher.code,
                );
                if (!responseTeacher) continue;
                contact.ownerId = responseTeacher.personID;
                transmitContacts.push(contact);
            }
            await saveContacts(transmitContacts);
        }

        return teachers;
    },
    {
        condition: (_, api) =>
            store.getState().db.teacher.status === "succeeded",
    },
);
export const updateTeacher = createAsyncThunk(
    "teacher/updateTeacher",
    async (payload: Teacher[]) => {
        const temp = new Map<Teacher, Contact[]>();
        for (const teacher of payload) {
            for (const contact of teacher.contacts ?? [])
                contact.ownerId = teacher.personID;
            temp.set(teacher, teacher.contacts ?? []);
            delete teacher.contacts;
        }
        const transmitContacts = Array.from(temp.values()).flat();
        const waits = [];

        waits.push(Datastore.putType<Teacher[]>("api/Teachers", payload));
        waits.push(saveContacts(transmitContacts));

        const [teachers] = await Promise.all(waits);

        return teachers;
    },
    {
        condition: (_, api) =>
            store.getState().db.teacher.status === "succeeded",
    },
);
export const archiveTeacher = createAsyncThunk(
    "teacher/archiveTeacher",
    (payload: Teacher[]) => {
        Datastore.post(
            "api/Teachers/Archive",
            payload.map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.teacher.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importTeachersFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Teachers/Import/TD/${id}`,
    );

    store.dispatch(resetTeacherStatus());
    await unwrappedDispatch(fetchTeachers());

    return result;
};

// We have to export the reducer as this is added to the state store
export default teacherSlice.reducer;
